// ─── Types ────────────────────────────────────────────────────────────────────

export type AthleteId = string;
export type RouteId = 'qual1' | 'qual2' | 'finals1';

export interface LeadAthlete {
  id: AthleteId;
  firstName: string;
  lastName: string;
  gender: 'male' | 'female';
  classId: string;
  groupId: string;   // references LeadSetup.groups[].id
  isLead: boolean;   // true = Lead, false = Top Rope
  country?: string;  // ISO 3166-1 alpha-2 code, e.g. "BE"
}

export interface LeadScore {
  athleteId: AthleteId;
  routeId: RouteId;
  holds: number | null;
  plus: boolean;
  top: boolean;
  dnf: boolean;
}

export interface LeadGroup {
  id: string;
  name: string;
  classIds?: string[];  // which individual class ids compete in this merging group
  gender?: 'men' | 'women'; // if set, only athletes of this gender compete in this group
  finalSpots?: number;  // how many athletes advance to finals (default 3)
}

export interface LeadClass {
  id: string;
  name: string;
  gender?: 'men' | 'women';
}

export interface LeadSetup {
  groups: LeadGroup[];
  classes: LeadClass[];
  locked: boolean;
}

export interface LeadCompetitionData {
  setup: LeadSetup;
  athletes: LeadAthlete[];
  /** Keyed by `${athleteId}_${routeId}` for atomic per-entry Firestore updates */
  scores: Record<string, LeadScore>;
}

export interface AthleteQualRow {
  athlete: LeadAthlete;
  score1: LeadScore | undefined;
  score2: LeadScore | undefined;
  value1: number;
  value2: number;
  rank1: number;
  rank2: number;
  combined: number;    // sqrt(rank1 * rank2)
  finalRank: number;  // rank within group after sorting by combined
}

export interface AthleteFinalsRow {
  athlete: LeadAthlete;
  score: LeadScore | undefined;
  value: number;
  rank: number;
}

// ─── Default athletes (from xlsx — Lead/Top Rope registrants) ─────────────────

export const DEFAULT_LEAD_ATHLETES: LeadAthlete[] = [
  // ── Male ──────────────────────────────────────────────────────────────────
  { id: 'lm1',  firstName: 'Matej',         lastName: 'Arh',              gender: 'male',   classId: 'AL1', groupId: '', isLead: true  },
  { id: 'lm2',  firstName: 'Illya',         lastName: 'Smirnov',          gender: 'male',   classId: 'AL1', groupId: '', isLead: false },
  { id: 'lm3',  firstName: 'Mykhailo',      lastName: 'Yavorskyi',        gender: 'male',   classId: 'AL1', groupId: '', isLead: false },
  { id: 'lm4',  firstName: 'Frederik',      lastName: 'Leys',             gender: 'male',   classId: 'AL2', groupId: '', isLead: true  },
  { id: 'lm5',  firstName: 'David',         lastName: 'Kammerer',         gender: 'male',   classId: 'AL2', groupId: '', isLead: true  },
  { id: 'lm6',  firstName: 'Oleksiy',       lastName: 'Smirnov',          gender: 'male',   classId: 'AL2', groupId: '', isLead: false },
  { id: 'lm7',  firstName: 'Matthew',       lastName: 'Styles-West',      gender: 'male',   classId: 'AU3', groupId: '', isLead: true  },
  { id: 'lm8',  firstName: 'Aldrik',        lastName: 'Bethke',           gender: 'male',   classId: 'AU3', groupId: '', isLead: true  },
  { id: 'lm9',  firstName: 'Tristan',       lastName: 'Stenuit',          gender: 'male',   classId: 'B1',  groupId: '', isLead: true  },
  { id: 'lm10', firstName: 'Maurice',       lastName: 'Sleuwaegen',       gender: 'male',   classId: 'B2',  groupId: '', isLead: false },
  { id: 'lm11', firstName: 'Tom',           lastName: 'Callewaert',       gender: 'male',   classId: 'B2',  groupId: '', isLead: true  },
  { id: 'lm12', firstName: 'Kevin',         lastName: 'Leveque',          gender: 'male',   classId: 'B3',  groupId: '', isLead: true  },
  { id: 'lm13', firstName: 'Jeffrey',       lastName: 'Mellenthin',       gender: 'male',   classId: 'RP2', groupId: '', isLead: true  },
  { id: 'lm14', firstName: 'Bas',           lastName: 'Vroman',           gender: 'male',   classId: 'RP2', groupId: '', isLead: false },
  { id: 'lm15', firstName: 'Jamie',         lastName: 'Barendrecht',      gender: 'male',   classId: 'RP3', groupId: '', isLead: true  },
  { id: 'lm16', firstName: 'Cesare',        lastName: 'Cunico',           gender: 'male',   classId: 'RP3', groupId: '', isLead: false },
  { id: 'lm17', firstName: 'Jannes Thies',  lastName: 'Gick',             gender: 'male',   classId: 'RP3', groupId: '', isLead: false },
  { id: 'lm18', firstName: 'Jeroen',        lastName: 'Debrock',          gender: 'male',   classId: 'RP1', groupId: '', isLead: false },
  { id: 'lm19', firstName: 'Ken',           lastName: 'Nagels',           gender: 'male',   classId: 'RP1', groupId: '', isLead: false },
  { id: 'lm20', firstName: 'Paul',          lastName: 'Martin',           gender: 'male',   classId: 'RP1', groupId: '', isLead: false },
  { id: 'lm21', firstName: 'Andries Hille', lastName: 'Oudijk',           gender: 'male',   classId: 'RP1', groupId: '', isLead: false },
  // ── Female ────────────────────────────────────────────────────────────────
  { id: 'lf1',  firstName: 'Sarah',         lastName: 'Larcombe',         gender: 'female', classId: 'AL2', groupId: '', isLead: true  },
  { id: 'lf2',  firstName: 'Manca',         lastName: 'Smrekar',          gender: 'female', classId: 'AU3', groupId: '', isLead: false },
  { id: 'lf3',  firstName: 'Tanja',         lastName: 'Glušič',           gender: 'female', classId: 'B1',  groupId: '', isLead: false },
  { id: 'lf4',  firstName: 'Luisa',         lastName: 'Grube',            gender: 'female', classId: 'B2',  groupId: '', isLead: true  },
  { id: 'lf5',  firstName: 'Nataliia',      lastName: 'Telehei',          gender: 'female', classId: 'B2',  groupId: '', isLead: false },
  { id: 'lf6',  firstName: 'Linn',          lastName: 'Poston',           gender: 'female', classId: 'B3',  groupId: '', isLead: true  },
  { id: 'lf7',  firstName: 'Anita',         lastName: 'Aggarwal',         gender: 'female', classId: 'RP3', groupId: '', isLead: true  },
  { id: 'lf8',  firstName: 'Jadwiga',       lastName: 'Gąsienica-Chmiel', gender: 'female', classId: 'RP3', groupId: '', isLead: false },
  { id: 'lf9',  firstName: 'Crystal',       lastName: 'Martin',           gender: 'female', classId: 'RP3', groupId: '', isLead: true  },
  { id: 'lf10', firstName: 'Tatjana',       lastName: 'Nußer',            gender: 'female', classId: 'RP3', groupId: '', isLead: false },
  { id: 'lf11', firstName: 'Beki',          lastName: 'Roberts',          gender: 'female', classId: 'RP3', groupId: '', isLead: false },
  { id: 'lf12', firstName: 'Nat',           lastName: 'Vorel',            gender: 'female', classId: 'RP3', groupId: '', isLead: true  },
  { id: 'lf13', firstName: 'Hannah',        lastName: 'Zook',             gender: 'female', classId: 'RP1', groupId: '', isLead: false },
  { id: 'lf14', firstName: 'Melissa',       lastName: 'Ruiz',             gender: 'female', classId: 'RP1', groupId: '', isLead: true  },
  { id: 'lf15', firstName: 'Manon',         lastName: 'Voiturier',        gender: 'female', classId: 'RP1', groupId: '', isLead: false },
  { id: 'lf16', firstName: 'Nancy',         lastName: 'Gilliams',         gender: 'female', classId: 'RP1', groupId: '', isLead: true  },
  { id: 'lf17', firstName: 'Eva',           lastName: 'Mol',              gender: 'female', classId: 'RP1', groupId: '', isLead: false },
  { id: 'lf18', firstName: 'Regina',        lastName: 'Fernández',        gender: 'female', classId: 'RP1', groupId: '', isLead: false },
];

export const DEFAULT_LEAD_CLASSES: LeadClass[] = [
  { id: 'AL1', name: 'AL1' }, { id: 'AL2', name: 'AL2' },
  { id: 'AU2', name: 'AU2' }, { id: 'AU3', name: 'AU3' },
  { id: 'B1',  name: 'B1'  }, { id: 'B2',  name: 'B2'  }, { id: 'B3',  name: 'B3'  },
  { id: 'RP1', name: 'RP1' }, { id: 'RP2', name: 'RP2' }, { id: 'RP3', name: 'RP3' },
];

export function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 5);
}

export function getDefaultData(): LeadCompetitionData {
  return {
    setup: { groups: [], classes: DEFAULT_LEAD_CLASSES.map(c => ({ ...c })), locked: false },
    athletes: DEFAULT_LEAD_ATHLETES.map(a => ({ ...a })),
    scores: {},
  };
}

// ─── Score map helpers ────────────────────────────────────────────────────────

export function scoreKey(athleteId: AthleteId, routeId: RouteId): string {
  return `${athleteId}_${routeId}`;
}

export function getScore(
  scores: Record<string, LeadScore>,
  athleteId: AthleteId,
  routeId: RouteId,
): LeadScore | undefined {
  return scores[scoreKey(athleteId, routeId)];
}

export function setScore(
  scores: Record<string, LeadScore>,
  athleteId: AthleteId,
  routeId: RouteId,
  patch: Partial<Omit<LeadScore, 'athleteId' | 'routeId'>>,
): Record<string, LeadScore> {
  const key = scoreKey(athleteId, routeId);
  const base: LeadScore = scores[key] ?? { athleteId, routeId, holds: null, plus: false, top: false, dnf: false };
  return { ...scores, [key]: { ...base, ...patch } };
}

export function mergeScore(
  scores: Record<string, LeadScore>,
  athleteId: AthleteId,
  routeId: RouteId,
  patch: Partial<Omit<LeadScore, 'athleteId' | 'routeId'>>,
): LeadScore {
  const key = scoreKey(athleteId, routeId);
  const base: LeadScore = scores[key] ?? { athleteId, routeId, holds: null, plus: false, top: false, dnf: false };
  return { ...base, ...patch };
}

// ─── Score formatting ─────────────────────────────────────────────────────────

/** Numeric performance value — higher = better. -1 = no score / DNF. Infinity = TOP. */
export function scoreValue(score: LeadScore | undefined): number {
  if (!score) return -1;
  if (score.top) return Infinity;
  if (score.dnf) return -1;
  if (score.holds === null) return -1;
  return score.holds + (score.plus ? 0.5 : 0);
}

export function formatLeadScore(score: LeadScore | undefined): string {
  if (!score) return '—';
  if (score.top) return 'TOP';
  if (score.dnf) return 'DNF';
  if (score.holds === null) return '—';
  return `${score.holds}${score.plus ? '+' : ''}`;
}

export function formatRank(rank: number): string {
  return rank % 1 === 0 ? String(rank) : rank.toFixed(1);
}

export function formatCombined(combined: number): string {
  return isFinite(combined) ? combined.toFixed(3) : '∞';
}

// ─── Ranking engine ───────────────────────────────────────────────────────────

/**
 * Assigns ranks (1-based, higher value = rank 1) with tie-averaging.
 * Example: [50, 50, 40] → [1.5, 1.5, 3]
 * Example: [50, 50, 50] → [2, 2, 2]
 */
export function rankWithTies(values: number[]): number[] {
  const n = values.length;
  if (n === 0) return [];

  const indices = Array.from({ length: n }, (_, i) => i);
  indices.sort((a, b) => {
    const va = values[a], vb = values[b];
    if (va === vb) return 0;
    if (va === Infinity) return -1;
    if (vb === Infinity) return 1;
    return vb - va; // descending
  });

  const ranks = new Array<number>(n).fill(0);
  let i = 0;
  while (i < n) {
    const v = values[indices[i]];
    let j = i + 1;
    while (j < n && values[indices[j]] === v) j++;
    // 1-based positions i+1 .. j, average = (i+1 + j) / 2
    const avgRank = (i + 1 + j) / 2;
    for (let k = i; k < j; k++) ranks[indices[k]] = avgRank;
    i = j;
  }
  return ranks;
}

function computeQualRankingsForAthletes(
  data: LeadCompetitionData,
  athletes: LeadAthlete[],
): AthleteQualRow[] {
  if (athletes.length === 0) return [];

  const s1 = athletes.map(a => getScore(data.scores, a.id, 'qual1'));
  const s2 = athletes.map(a => getScore(data.scores, a.id, 'qual2'));
  const v1 = s1.map(scoreValue);
  const v2 = s2.map(scoreValue);
  const r1 = rankWithTies(v1);
  const r2 = rankWithTies(v2);

  const rows: AthleteQualRow[] = athletes.map((athlete, i) => ({
    athlete,
    score1: s1[i], score2: s2[i],
    value1: v1[i], value2: v2[i],
    rank1: r1[i],  rank2: r2[i],
    combined: Math.sqrt(r1[i] * r2[i]),
    finalRank: 0,
  }));

  rows.sort((a, b) => a.combined - b.combined);

  const finalRanks = rankWithTies(rows.map(r => -r.combined));
  rows.forEach((r, i) => { r.finalRank = finalRanks[i]; });

  return rows;
}

export function computeQualRankings(
  data: LeadCompetitionData,
  groupId: string,
): AthleteQualRow[] {
  return computeQualRankingsForAthletes(
    data,
    data.athletes.filter(a => a.groupId === groupId),
  );
}

export function computeAllGroupQualRankings(
  data: LeadCompetitionData,
): Array<{ group: LeadGroup | null; groupId: string; rows: AthleteQualRow[] }> {
  const result: Array<{ group: LeadGroup | null; groupId: string; rows: AthleteQualRow[] }> = [];

  // Athletes in explicit merging groups
  const explicitGroupIds = Array.from(new Set(
    data.athletes.filter(a => a.groupId).map(a => a.groupId),
  ));
  explicitGroupIds.forEach(gid => {
    const rows = computeQualRankings(data, gid);
    if (rows.length > 0) {
      result.push({ group: data.setup.groups.find(g => g.id === gid) ?? null, groupId: gid, rows });
    }
  });

  // Athletes not in any group: virtual group per (classId, gender)
  const ungrouped = data.athletes.filter(a => !a.groupId);
  const ungroupedClassIds = Array.from(new Set(ungrouped.map(a => a.classId)));
  ungroupedClassIds.forEach(classId => {
    const classAthletes = ungrouped.filter(a => a.classId === classId);
    const className = data.setup.classes.find(c => c.id === classId)?.name ?? classId;
    (['male', 'female'] as const).forEach(gender => {
      const athletes = classAthletes.filter(a => a.gender === gender);
      if (athletes.length === 0) return;
      const groupGender = gender === 'male' ? 'men' : 'women';
      const name = `${className} ${groupGender === 'men' ? 'Men' : 'Women'}`;
      const virtualGroupId = `__solo_${classId}_${gender}`;
      const virtualGroup: LeadGroup = { id: virtualGroupId, name, classIds: [classId], gender: groupGender };
      const rows = computeQualRankingsForAthletes(data, athletes);
      if (rows.length > 0) result.push({ group: virtualGroup, groupId: virtualGroupId, rows });
    });
  });

  return result.sort((a, b) => (a.group?.name ?? a.groupId).localeCompare(b.group?.name ?? b.groupId));
}

export function computeFinalsRankings(data: LeadCompetitionData): AthleteFinalsRow[] {
  const athletes = data.athletes.filter(a => {
    const s = getScore(data.scores, a.id, 'finals1');
    return s && (s.top || s.dnf || s.holds !== null);
  });
  if (athletes.length === 0) return [];

  const scores = athletes.map(a => getScore(data.scores, a.id, 'finals1'));
  const values = scores.map(scoreValue);
  const ranks  = rankWithTies(values);

  const rows: AthleteFinalsRow[] = athletes.map((athlete, i) => ({
    athlete, score: scores[i], value: values[i], rank: ranks[i],
  }));
  rows.sort((a, b) => a.rank - b.rank);
  return rows;
}

function computeFinalsRankingsForAthletes(
  data: LeadCompetitionData,
  athletes: LeadAthlete[],
): AthleteFinalsRow[] {
  const filtered = athletes.filter(a => {
    const s = getScore(data.scores, a.id, 'finals1');
    return s && (s.top || s.dnf || s.holds !== null);
  });
  if (filtered.length === 0) return [];
  const scores = filtered.map(a => getScore(data.scores, a.id, 'finals1'));
  const values = scores.map(scoreValue);
  const ranks  = rankWithTies(values);
  const rows: AthleteFinalsRow[] = filtered.map((athlete, i) => ({
    athlete, score: scores[i], value: values[i], rank: ranks[i],
  }));
  rows.sort((a, b) => a.rank - b.rank);
  return rows;
}

export function computeFinalsRankingsByGroup(
  data: LeadCompetitionData,
): Array<{ group: LeadGroup | null; groupId: string; rows: AthleteFinalsRow[] }> {
  const result: Array<{ group: LeadGroup | null; groupId: string; rows: AthleteFinalsRow[] }> = [];

  // Athletes in explicit merging groups
  const explicitGroupIds = Array.from(new Set(
    data.athletes.filter(a => a.groupId).map(a => a.groupId),
  ));
  explicitGroupIds.forEach(gid => {
    const rows = computeFinalsRankingsForAthletes(
      data,
      data.athletes.filter(a => a.groupId === gid),
    );
    if (rows.length > 0) {
      result.push({ group: data.setup.groups.find(g => g.id === gid) ?? null, groupId: gid, rows });
    }
  });

  // Athletes not in any group: virtual group per (classId, gender)
  const ungrouped = data.athletes.filter(a => !a.groupId);
  const ungroupedClassIds = Array.from(new Set(ungrouped.map(a => a.classId)));
  ungroupedClassIds.forEach(classId => {
    const classAthletes = ungrouped.filter(a => a.classId === classId);
    const className = data.setup.classes.find(c => c.id === classId)?.name ?? classId;
    (['male', 'female'] as const).forEach(gender => {
      const athletes = classAthletes.filter(a => a.gender === gender);
      if (athletes.length === 0) return;
      const groupGender = gender === 'male' ? 'men' : 'women';
      const name = `${className} ${groupGender === 'men' ? 'Men' : 'Women'}`;
      const virtualGroupId = `__solo_${classId}_${gender}`;
      const virtualGroup: LeadGroup = { id: virtualGroupId, name, classIds: [classId], gender: groupGender };
      const rows = computeFinalsRankingsForAthletes(data, athletes);
      if (rows.length > 0) result.push({ group: virtualGroup, groupId: virtualGroupId, rows });
    });
  });

  return result.sort((a, b) => (a.group?.name ?? a.groupId).localeCompare(b.group?.name ?? b.groupId));
}
